import { useEffect, useRef, useState } from "react";

import { resolveId } from "@medplum/core";
import { Patient, Reference } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, CloseButton, Combobox, Group, Input, InputBase, useCombobox } from "@mantine/core";

import { IconCheck } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import {
  getRelatedNewbornsForMother,
  RelatedNewborn,
} from "@/newborn-patient-linking/services/get-related-newborns-for-mother";
import { formatDateInTimezone, TIME_FORMAT, usDateFormat } from "@/utils/datetime";

interface NewbornPickerProps {
  motherReference: Reference<Patient>;
  practiceOrganizationId: string;
  value: Reference<Patient> | undefined;
  setValue: (value: Reference<Patient> | undefined) => void;
  disabled?: boolean;
  inputTestId?: string;
}

const LOCKED_DESCRIPTION = "Can't be changed once this encounter has been saved";

/**
 * Always anchors the displayed date to `birthDate`, since that's the value shown for this newborn
 * everywhere else in the app — `birthDateTime` only adds a time-of-day alongside it, to tell
 * same-day twins apart, rather than replacing the date. The time is rendered in `deliveryTimezone`
 * (the same zone `birthDate` was already extracted in), not the viewer's own timezone: converting
 * to the viewer's timezone could show a time that looks like it belongs to a different day than
 * `birthDate` does. The time is omitted whenever `deliveryTimezone` didn't resolve, rather than
 * guessing a zone. Appends the newborn's name after a dash once Birth Details has set one.
 */
export function formatNewbornOptionLabel(newborn: RelatedNewborn): string {
  const dateLabel = usDateFormat(newborn.birthDate) || "Birth date not yet recorded";
  const timeLabel =
    newborn.birthDateTime && newborn.deliveryTimezone
      ? ` ${formatDateInTimezone(newborn.birthDateTime, newborn.deliveryTimezone, TIME_FORMAT)}`
      : "";
  const dateTimeLabel = `${dateLabel}${timeLabel}`;
  return newborn.name ? `${dateTimeLabel} — ${newborn.name}` : dateTimeLabel;
}

/**
 * Picks a mother's newborn for a standalone newborn-creation flow (Newborn IPP or Newborn
 * Postpartum), via `getRelatedNewbornsForMother`. Modeled on `EpisodeOfCareSelect`'s Combobox
 * pattern, minus its "create new"/"manage" affordances — a newborn is only ever created by the
 * birth-charting flow.
 */
export default function NewbornPicker({
  motherReference,
  practiceOrganizationId,
  value,
  setValue,
  disabled = false,
  inputTestId,
}: NewbornPickerProps) {
  const medplum = useMedplum();
  const motherId = resolveId(motherReference);
  const [newborns, setNewborns] = useState<RelatedNewborn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const requestIdRef = useRef(0);
  const previousMotherIdRef = useRef(motherId);

  // Clears a selection made for a previous mother once the resolved mother actually changes, so a
  // stale newborn reference can never be submitted against the wrong mother. Skipped on mount (the
  // ref starts equal to the first motherId), since mount can be re-hydrating a value already saved
  // for this same mother.
  useEffect(() => {
    if (previousMotherIdRef.current !== motherId) {
      previousMotherIdRef.current = motherId;
      setValue(undefined);
    }
  }, [motherId, setValue]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (!motherId) {
      setNewborns([]);
      setHasError(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    (async () => {
      try {
        const relatedNewborns = await getRelatedNewbornsForMother(
          medplum,
          motherReference,
          practiceOrganizationId,
        );
        if (requestIdRef.current !== requestId) return;
        setNewborns(relatedNewborns);
      } catch {
        if (requestIdRef.current !== requestId) return;
        setHasError(true);
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    })();
    // motherReference is recreated on every render by callers; motherId is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, motherId, practiceOrganizationId]);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  if (isLoading) {
    return <Loading height={96} />;
  }

  if (hasError) {
    return (
      <TextWrapper size="sm" c="red.7">
        Couldn&apos;t load this client&apos;s newborns.
      </TextWrapper>
    );
  }

  if (newborns.length === 0) {
    return (
      <TextWrapper size="sm" c="gray.7">
        No newborns have been created for this client yet.
      </TextWrapper>
    );
  }

  const selectedNewbornId = resolveId(value);
  const selectedNewborn = newborns.find((newborn) => newborn.patientId === selectedNewbornId);

  const options = newborns.map((newborn) => (
    <Combobox.Option key={newborn.patientId} value={newborn.patientId}>
      <Group align="center" justify="space-between">
        <TextWrapper>{formatNewbornOptionLabel(newborn)}</TextWrapper>
        {selectedNewbornId === newborn.patientId ? <IconCheck /> : null}
      </Group>
    </Combobox.Option>
  ));

  return (
    <Box>
      <Combobox
        store={combobox}
        onOptionSubmit={(newbornPatientId) => {
          const newborn = newborns.find((candidate) => candidate.patientId === newbornPatientId);
          setValue(newborn?.patientReference);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            pointer
            type="button"
            label="Newborn"
            component="button"
            disabled={disabled}
            data-testid={inputTestId}
            onClick={() => combobox.toggleDropdown()}
            description={disabled ? LOCKED_DESCRIPTION : undefined}
            rightSectionPointerEvents={selectedNewborn && !disabled ? "all" : "none"}
            rightSection={
              selectedNewborn && !disabled ? (
                <CloseButton
                  size="sm"
                  aria-label="Clear value"
                  onClick={() => setValue(undefined)}
                  onMouseDown={(event) => event.preventDefault()}
                />
              ) : (
                <Combobox.Chevron />
              )
            }
          >
            {selectedNewborn ? (
              formatNewbornOptionLabel(selectedNewborn)
            ) : (
              <Input.Placeholder>Select</Input.Placeholder>
            )}
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>{options}</Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
}
